<!--
// app/components/pages/region-matches-match-homeTeam-vs-awayTeam/MatchEventsEventMobile.vue
-->
<script setup lang="ts">
import type { Player } from "~/src/player/domain/Player";
import type { Event } from "~/src/match/domain/models/Event";
import matchUiService from "~/src/match/infrastructure/ui-services/matchUiService";

type Props = {
  event: Event;
  players: Player[];
  isAwayTeam?: boolean;
  seasonClusterId: number;
};
const props = defineProps<Props>();

const { formatEventMinute, getMatchingCode, getMatchingCodeImage } = matchUiService();

const findPlayerById = (playerId: number): Player | undefined => {
  return props.players.find(player => player.id === playerId);
};
</script>

<template>
  <div :class="['event-wrapper', isAwayTeam ? 'ms-auto' : 'me-auto']">
    <div
      :class="[
        'w-full flex gap-x-1 items-center h-fit px-1',
        isAwayTeam ? '!flex-row-reverse justify-end' : 'justify-end',
      ]"
    >
      <div v-if="!isAwayTeam" class="event-tooltip event-tooltip-home">
        <div class="bg-[#242424] rounded-lg">
          <p
            class="text-[10px] text-white bg-gray-800 px-1 py-0.5 text-center"
            :class="
              findPlayerById(<number>event.playerId)?.jersey ||
              findPlayerById(<number>event.playerId)?.jersey === 0
                ? 'rounded-t-lg'
                : 'rounded-lg'
            "
          >
            {{ $t(`timeline.${getMatchingCode(event.parameterAppString)}`) }}
          </p>
          <div
            v-if="getMatchingCode(event.parameterAppString) === 'GOL-RPE'"
            class="flex gap-1 text-sm justify-center items-center px-1 py-0.5"
          >
            <p class="font-medium text-gray-400">
              {{ $t("timeline.GOL-RPE.description") }}
            </p>
          </div>
          <div v-else-if="event.playerId" class="flex gap-1 text-sm justify-center items-center px-1 py-0.5">
            <p v-if="getMatchingCode(event.parameterAppString) !== 'GOL-RPE'" class="font-bold text-gray-500">
              #{{ findPlayerById(event.playerId)?.jersey }}
            </p>
            <p class="font-medium text-gray-400">
              {{
                getMatchingCode(event.parameterAppString) === "GOL-RPE"
                  ? $t("timeline.GOL-RPE.description")
                  : findPlayerById(event.playerId)?.shortName
              }}
            </p>
          </div>
          <div
            v-else-if="event.opponentPlayerId && event.parameterAppString.includes('CARD-RPE-ACT,')"
            class="flex gap-1 text-sm justify-center items-center px-1 py-0.5"
          >
            <p v-if="getMatchingCode(event.parameterAppString) !== 'GOL-RPE'" class="font-bold text-gray-500">
              #{{ findPlayerById(event.opponentPlayerId)?.jersey }}
            </p>
            <p class="font-medium text-gray-400">
              {{
                getMatchingCode(event.parameterAppString) === "GOL-RPE"
                  ? $t("timeline.GOL-RPE.description")
                  : findPlayerById(event.opponentPlayerId)?.shortName
              }}
            </p>
          </div>
        </div>
        <svg
          class="-rotate-90 rtl:rotate-90 -ms-1"
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="6"
          viewBox="0 0 18 8"
          fill="none"
        >
          <path
            d="M8.29289 7.29289L1.29289 0.292893C1.10536 0.105357 0.851003 0 0.585786 0H17.4142C17.149 0 16.8946 0.105356 16.7071 0.292892L9.70711 7.29289C9.31658 7.68342 8.68342 7.68342 8.29289 7.29289Z"
            fill="#242424"
          />
        </svg>
      </div>
      <div v-else class="event-tooltip event-tooltip-away">
        <svg
          class="-rotate-90 rtl:rotate-90 -me-1"
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="6"
          viewBox="0 0 18 8"
          fill="none"
        >
          <path
            d="M9.70711 0.707105L16.7071 7.70711C16.8946 7.89464 17.149 8 17.4142 8L0.585788 8C0.851005 8 1.10536 7.89464 1.29289 7.70711L8.29289 0.707105C8.68342 0.316582 9.31658 0.316581 9.70711 0.707105Z"
            fill="#242424"
          />
        </svg>
        <div class="bg-gray-800 rounded-lg">
          <p
            class="text-[10px] text-white bg-[#242424] px-1 py-0.5 text-center"
            :class="findPlayerById(<number>event.playerId)?.jersey ? 'rounded-t-lg' : 'rounded-lg'"
          >
            {{ $t(`timeline.${getMatchingCode(event.parameterAppString)}`) }}
          </p>
          <div
            v-if="getMatchingCode(event.parameterAppString) === 'GOL-RPE'"
            class="flex gap-1 text-sm justify-center items-center px-1 py-0.5"
          >
            <p class="font-medium text-gray-400">
              {{ $t("timeline.GOL-RPE.description") }}
            </p>
          </div>
          <div v-else-if="event.playerId" class="flex gap-1 text-sm justify-center items-center px-1 py-0.5">
            <p v-if="getMatchingCode(event.parameterAppString) !== 'GOL-RPE'" class="font-bold text-gray-500">
              #{{ findPlayerById(event.playerId)?.jersey }}
            </p>
            <p class="font-medium text-gray-400">
              {{
                getMatchingCode(event.parameterAppString) === "GOL-RPE"
                  ? $t("timeline.GOL-RPE.description")
                  : findPlayerById(event.playerId)?.shortName
              }}
            </p>
          </div>
          <div
            v-else-if="event.opponentPlayerId && event.parameterAppString.includes('CARD-RPE-ACT,')"
            class="flex gap-1 text-sm justify-center items-center px-1 py-0.5"
          >
            <p v-if="getMatchingCode(event.parameterAppString) !== 'GOL-RPE'" class="font-bold text-gray-500">
              #{{ findPlayerById(event.opponentPlayerId)?.jersey }}
            </p>
            <p class="font-medium text-gray-400">
              {{
                getMatchingCode(event.parameterAppString) === "GOL-RPE"
                  ? $t("timeline.GOL-RPE.description")
                  : findPlayerById(event.opponentPlayerId)?.shortName
              }}
            </p>
          </div>
        </div>
      </div>
      <p class="text-gray-500 text-xs">{{ formatEventMinute(event, seasonClusterId) }}</p>
      <!--EVENT ICON-->

      <img
        :src="`/img/match-timeline-icons/${getMatchingCodeImage(event.parameterAppString)}.png`"
        height="20"
        width="auto"
        class="w-auto h-[20px]"
        :alt="$t(`timeline.${getMatchingCode(event.parameterAppString)}`)"
      />
      <div class="event-line"></div>
    </div>
  </div>
</template>

<style scoped>
.event-wrapper {
  @apply md:hidden max-w-[50%] my-1.5;
}

.event-tooltip {
  @apply w-max max-w-full z-10;
}

.event-tooltip-home {
  @apply flex items-center gap-0 right-32;
}

.event-tooltip-away {
  @apply flex items-center gap-0 left-32;
}

.event-line {
  @apply w-[8px] h-[1px] bg-white;
}
</style>
